import React, { createContext, useContext, useEffect, useRef, useCallback } from 'react'
import { TourProvider as ReactTourProvider, useTour as useReactTour } from '@reactour/tour'
import { TourStep } from '../atomic-components/molecules/tourStep'
import storageService from '../services/storageService'
import { isBrowser } from '../utils/generic'
import { useEventTracking } from './eventTrackingProvider'

const TourContext = createContext()

const defaultTourConfig = {
  ContentComponent: TourStep,
  styles: {
    popover: (base) => ({
      ...base,
      background: 'transparent',
      padding: '10px',
      borderRadius: 0,
      boxShadow: 'none',
      border: 'none',
      color: 'inherit',
    }),
    maskArea: (base) => ({
      ...base,
      rx: 8,
    }),
    badge: (base) => ({
      ...base,
      display: 'none',
    }),
  },
}

export const TourProvider = ({ children, ...props }) => {

  const tourConfig = {
    ...defaultTourConfig,
    ...props,
  }

  const value = {
    config: tourConfig,
  }

  return (
    <TourContext.Provider value={value}>
      <ReactTourProvider disableInteraction {...tourConfig}>
        {children}
      </ReactTourProvider>
    </TourContext.Provider>
  )
}

export const useTourContext = () => {
  const context = useContext(TourContext)
  
  if (!context) {
    throw new Error('useTourContext must be used inside a TourProvider')
  }

  const tour = useReactTour()
  
  return {
    ...context,
    ...tour,
  }
}

/**
 * Custom hook to manage a tour.
 * @param {Object} options - Configuration options for the tour
 * @param {Array} options.steps - Array of tour steps
 * @param {boolean} [options.enabled=true] - Whether the tour is enabled (feature flags, etc.)
 * @param {string} [options.location] - Location where the tour is shown (for tracking)
 * @param {string} [options.storageKey] - If provided, tour will only show once and be saved to storage
 */
export const useTour = ({ steps, enabled = true, location = null, storageKey = null }) => {
  const { setIsOpen, setCurrentStep, setSteps, isOpen, currentStep } = useReactTour()
  const { trackEvent, EVENT_NAMES } = useEventTracking()
    const wasOpenRef = useRef(false)

  const trackTourEvent = useCallback((action) => {
    trackEvent(EVENT_NAMES.ONBOARDING_TOUR, {
      action,
      step_number: currentStep + 1,
      site_location: location,
    }, true)
  }, [trackEvent, EVENT_NAMES.ONBOARDING_TOUR, currentStep, location])

  useEffect(() => {
    if (!isBrowser()) return
    if (!enabled) return
    if (!steps || steps.length === 0) return
    
    if (storageKey) {
      const tourAlreadyShown = storageService.getValue(storageKey)
      if (tourAlreadyShown) return
    }

    setSteps(steps)
    setCurrentStep(0)
    setIsOpen(true)
    
  }, [storageKey, steps, enabled, setSteps, setCurrentStep, setIsOpen])

  useEffect(() => {
    const isTourOpeningForFirstTime = isOpen && !wasOpenRef.current
    const isTourClosingAfterBeingOpen = !isOpen && wasOpenRef.current
    
    if (isTourOpeningForFirstTime) {
      trackTourEvent('shown')
      wasOpenRef.current = true
    }
    
    if (isTourClosingAfterBeingOpen) {
      trackTourEvent('dismissed')
      
      if (storageKey) {
        storageService.setValue(storageKey, 'true')
      }
      wasOpenRef.current = false
    }
  }, [isOpen, storageKey, trackTourEvent])

  useEffect(() => {
    const shouldTrackStepChange = isOpen && wasOpenRef.current && currentStep > 0
    
    if (shouldTrackStepChange) {
      trackTourEvent('shown')
    }
  }, [isOpen, currentStep, trackTourEvent])
}

export { useTour as useReactTour } from '@reactour/tour' 